// GENERATED by helm-console/scripts/ensure-icons.mjs — DO NOT EDIT.
// Source of truth: helm-bridge/src/client/icons-synthwave.ts
// (itself generated from lucide-static by helm-bridge/scripts/gen-icon-sprite.mjs).
// Regenerated on every console build; edits here are overwritten.
// The console and the release UI share ONE icon set on purpose — see the
// header of ensure-icons.mjs.
// icons-synthwave.ts — SYNTHWAVE '88's ICON SET.
//
// THE SHARED ID CONTRACT. A style does not get its own id space: it answers
// the SAME symbol ids the Blueprint sprite defines, so every call site in the
// app is unchanged and a style switch is a sprite swap, not a refactor. Two
// rules make that safe, and scripts/check-icon-refs.mjs enforces both with
// measured counts:
//
//   1. NO ORPHANS. Every id below must exist in the Blueprint sprite. An id
//      only this set defines would render for SYNTHWAVE and blank for
//      everyone else — the silent-blank failure the icons gate refuses.
//   2. NO FALLBACKS (owner ruling 2026-09-07). The old rule — "a set does not
//      have to cover all the ids, it only has to fall back by name" — is
//      RETIRED. The owner saw the Helm's nav rendering a mixed set in
//      production and ruled that a styled set is 100% its own glyphs or the
//      gate is red: `check-icon-refs.mjs` refuses any style with a Blueprint
//      fallback and no dated COVERAGE_DEBT row, and SYNTHWAVE carries no row.
//      This file therefore answers EVERY id the Blueprint sprite defines.
//
// THE DRAWING RULES (spec §6, R21). 1.5px stroke, BUTT CAPS, MITER JOINS,
// 24px grid, no fill plates ever — precision-drafted like a schematic, which
// is what separates this set from GLASS's round-capped optical family and
// from Destroyer's 2px square-terminal stencils. Stroke weight, caps and
// joins are NOT set here: styles.css sets them once on `.icon` under this
// style, so one rule governs the whole set.
//
// THE SET IS A STATE MACHINE, and it is the only one in the programme that
// is. Icons are UNLIT AT REST — `var(--ink-2)`, no bloom — and take their
// assigned neon hue plus the Tier-1 bloom only on hover / focus / active
// (spec §6). That replaces synthwave-1's constant `0 0 4px` glow on every
// icon in the application, which was an ambient glow that contradicted the
// style's own "glow marks state" rule. The state machine lives in the CSS;
// this file is the geometry it drives.
//
// WHAT THIS SET CUTS FIRST, and why these ids. SYNTHWAVE's thesis is the
// ground plane under a horizon, so the glyphs that carry it are the HORIZON
// family (the ring-stack sun, the horizon rule, the perspective grid, the
// plates), the METER family (everything that shows a level becomes VU
// segments — the style's one signature primitive, reused rather than
// re-invented), and the whole STATUS ladder plus the four LOCKED INTENT SHAPES
// (spec §2: neutral = plain outline, additive = plus, destructive = trash,
// error = triangle-bang — hue and shape are two channels, so CVD never
// collapses them). Those 31 were the original cut (PR #472).
//
// THE REST OF THE SET (the 96 ids drawn under the owner's 2026-09-07
// completion ruling) is cut to the SAME drawing rules, stated once so a future
// glyph has no room to drift:
//
//   * PURE OPEN LINE-WORK. No fill, ever — `fill: none` is set on `.icon` for
//     this style, so any shape that relies on a fill renders as an empty
//     silhouette. That is exactly how MAINFRAME's fallbacks went blank
//     (icons-mainframe.ts / PR #648): a style-level paint rule and a foreign
//     glyph's construction disagreeing in silence. Every path here reads as
//     art under a stroke-only rule.
//   * SQUARE CORNERS. Containers, plates, cards, panels and frames are closed
//     paths with hard 90° corners (`M3 4h18v16H3Z`), never `rx`-rounded
//     rects — the drafting-table corner is what separates this set from
//     GLASS's optical family and from Blueprint's Lucide radii.
//   * STRAIGHT LINES AND 45°s FIRST. An arc is drawn only where the subject
//     IS round (a dial face, a lamp, a graph node, a barrel). Everything a
//     schematic would rule with a straight-edge is ruled with one.
//   * ONE MOTIF, REUSED. The ring-stack sun is the brand mark; the VU segment
//     is every level; the horizon rule is every ground. A new glyph reaches
//     for one of those before inventing a primitive.
//   * SEMANTICS BEFORE STYLE. Two ids the Blueprint sprite draws with ONE
//     shared path stay aliases here when their call sites demand it —
//     `tack` is a refresh arrow (app.ts renders it as "Refresh usage meters"),
//     `gate` is a checked clipboard (section-tables.ts renders it on
//     acceptance criteria and validation checklists). Re-cutting those to
//     their literal nouns would have been a silent semantic regression that
//     no gate measures.

/** The brand mark: a wireframe RING-STACK SUN cut off by a horizon rule that
 * extends past it on both sides. Three stroked arcs, never a filled gradient
 * disc — that is the anti-cliché move the whole style rests on, and it reads
 * as a targeting reticle rather than album art. Also the artwork behind both
 * `helm` and `ship-wheel`, which Blueprint draws with one shared path. */
const RING_SUN =
  '<path d="M3 16h18" />' +
  '<path d="M20 16a8 8 0 0 0-16 0" />' +
  '<path d="M16.6 16a4.6 4.6 0 0 0-9.2 0" />' +
  '<path d="M13.6 16a1.6 1.6 0 0 0-3.2 0" />';

/** The bare marks, named once and shared by every id that IS that mark. The
 * Blueprint sprite does the same (its `check` and `st-check` are one path);
 * naming them here keeps the two copies from drifting apart in a later edit. */
const CHECK_MARK = '<path d="M4 12.5 9.5 18 20 6" />';
const CROSS_MARK = '<path d="M5.5 5.5 18.5 18.5" /> <path d="M18.5 5.5 5.5 18.5" />';

/** The single clockwise refresh arc + its corner bracket. `rotate-cw` and
 * `tack` are the same instrument at their call sites (app.ts renders `tack` as
 * "Refresh usage meters"), so they are the same glyph. */
const ROTATE_ARROW =
  '<path d="M20.5 12a8.5 8.5 0 1 1-2.6-6.1L21 8.5" /> <path d="M21 3.5V9h-5.5" />';

/** The clipboard plate + clip + check. `clipboard-check` and `gate` share it:
 * `gate` is rendered on acceptance criteria and validation checklists
 * (section-tables.ts), which is an APPROVAL, not a barrier. */
const CLIPBOARD_CHECK =
  '<path d="M5 4h14v17H5Z" /> <path d="M8.5 2.5h7V6h-7Z" />' +
  ' <path d="m8.5 13.5 2.5 2.6 4.5-5.1" />';

/** The isometric crate silhouette — a hard-edged hexagon with the top seam and
 * the front edge. `box`, `cargo` and `container` are the same crate seen three
 * ways (bare / strapped / ribbed), the way the corpus reads them. */
const ISO_CRATE =
  '<path d="M12 2.5 21 7v10l-9 4.5L3 17V7Z" /> <path d="m3 7 9 4.5 9-4.5" />' +
  ' <path d="M12 11.5v10" />';

/**
 * id -> symbol INNER markup (the `<symbol>` wrapper and the viewBox are added
 * by icons.ts, so a set cannot get either wrong).
 */
export const SYNTHWAVE_SYMBOLS: Record<string, string> = {
  // ---- the horizon family ---------------------------------------------
  helm: RING_SUN,
  'ship-wheel': RING_SUN,
  sun: RING_SUN,
  horizon:
    '<path d="M2 13h20" /> <path d="M17 13a5 5 0 0 0-10 0" />' +
    ' <path d="M2 18h6" /> <path d="M16 18h6" />',
  moon:
    '<path d="M17.5 4.2A9 9 0 1 0 20 14.6 7.2 7.2 0 0 1 17.5 4.2Z" />',
  porthole:
    '<circle cx="12" cy="12" r="8.5" /> <path d="M4.6 15h14.8" />' +
    ' <path d="M6.6 18.2h10.8" />',
  'grid-2x2':
    '<path d="M2 20h20" /> <path d="M12 4v16" /> <path d="M12 4 2 20" />' +
    ' <path d="M12 4l10 16" /> <path d="M6.6 13h10.8" /> <path d="M4.2 16.5h15.6" />',
  layers:
    '<path d="M12 3 22 8l-10 5L2 8Z" /> <path d="M2 12.5 12 17.5l10-5" />' +
    ' <path d="M2 17 12 22l10-5" />',
  wave:
    '<path d="M2 8h20" /> <path d="M3.5 13h17" /> <path d="M5 18h14" />',

  // ---- the meter family: everything that shows a level is VU segments ---
  signal:
    '<path d="M3 17v4" /> <path d="M8 13v8" /> <path d="M13 9v12" /> <path d="M18 4v17" />',
  chart:
    '<path d="M3 21V3" /> <path d="M3 21h18" />' +
    ' <path d="M7 17.5h2.4" /> <path d="M7 14.5h2.4" /> <path d="M7 11.5h2.4" />' +
    ' <path d="M12.4 17.5h2.4" /> <path d="M12.4 14.5h2.4" />' +
    ' <path d="M17.8 17.5h2.4" /> <path d="M17.8 14.5h2.4" /> <path d="M17.8 11.5h2.4" />' +
    ' <path d="M17.8 8.5h2.4" />',
  activity:
    '<path d="M2 12h4l2.5-7 4 14 3-7H22" />',
  gauge:
    '<path d="M3.5 18.5a9 9 0 1 1 17 0" /> <path d="M12 18.5 16.8 9.6" />' +
    ' <path d="M4.6 13.4 6.5 12.3" /> <path d="M12 5.2v2.2" /> <path d="M19.4 13.4 17.5 12.3" />',
  antenna:
    '<path d="M12 21V9" /> <path d="M8.5 6 12 9 15.5 6" />' +
    ' <path d="M5.5 3 12 8.4 18.5 3" /> <path d="M8 21h8" />',
  sonar:
    '<path d="M12 20V4" /> <path d="M4 20h16" />' +
    ' <path d="M7.4 20a4.6 4.6 0 0 1 9.2 0" /> <path d="M4 20a8 8 0 0 1 16 0" />',

  // ---- instruments -------------------------------------------------------
  compass:
    '<circle cx="12" cy="12" r="8.5" /> <path d="M12 3.5v2" /> <path d="M20.5 12h-2" />' +
    ' <path d="M12 20.5v-2" /> <path d="M3.5 12h2" /> <path d="M8.2 15.8 15.8 8.2" />' +
    ' <path d="M12 12 15.8 8.2" />',
  telescope:
    '<path d="M3 16.5 15.5 9" /> <path d="M5.5 20.8 18 13.3" /> <path d="M3 16.5 5.5 20.8" />' +
    ' <path d="M15.5 9 18 13.3" /> <path d="M17.2 8.4 21.6 5.8" /> <path d="M19.4 12.2 21.6 5.8" />',
  buoy:
    '<path d="M12 3v11" /> <path d="M12 3 6.5 6.5 12 10" /> <path d="M4 18h16" />' +
    ' <path d="M7 14h10l3 4H4Z" />',
  terminal:
    '<path d="M3 4h18v16H3Z" /> <path d="M7 9.5 10 12l-3 2.5" /> <path d="M12.5 15h4.5" />',

  // ---- the four LOCKED INTENT SHAPES (spec §2) --------------------------
  // Hue and shape are two independent channels, so a viewer with any form of
  // colour-vision deficiency still reads the intent from the silhouette.
  // neutral = plain outline · additive = plus · destructive = trash ·
  // error = triangle-bang (NOT a circle: the circle is neutral's).
  'circle-dot':
    '<circle cx="12" cy="12" r="8.5" /> <path d="M10.4 12h3.2" />',
  plus:
    '<path d="M12 4v16" /> <path d="M4 12h16" />',
  'trash-2':
    '<path d="M3.5 6.5h17" /> <path d="M9 6.5V3.5h6v3" /> <path d="M5.5 6.5 6.8 21h10.4l1.3-14.5" />' +
    ' <path d="M10 10.5v6.5" /> <path d="M14 10.5v6.5" />',
  'circle-alert':
    '<path d="M12 3 22.5 21H1.5Z" /> <path d="M12 9.5v5" /> <path d="M12 17.4v.2" />',

  // ---- the status ladder -------------------------------------------------
  // Schematic, square-cornered, and readable at 12px: the marks the eye lands
  // on most often in a fleet grid.
  'st-dash': '<path d="M5 12h14" />',
  'st-ring': '<path d="M4.5 4.5h15v15h-15Z" />',
  'st-half': '<path d="M4.5 4.5h15v15h-15Z" /> <path d="M4.5 12h7.5V4.5" />',
  'st-check': '<path d="M4 12.5 9.5 18 20 6" />',
  'st-seal': '<path d="M12 2.5 20.5 7v10L12 21.5 3.5 17V7Z" /> <path d="M8 12.2 11 15.4 16.4 9" />',
  'st-cross': '<path d="M5.5 5.5 18.5 18.5" /> <path d="M18.5 5.5 5.5 18.5" />',
  'st-pause': '<path d="M9 5v14" /> <path d="M15 5v14" />',
  'circle-check':
    '<path d="M4.5 4.5h15v15h-15Z" /> <path d="M7.6 12.2 10.6 15.4 16.6 8.6" />',

  // =====================================================================
  // THE COMPLETION CUT (owner ruling 2026-09-07) — the remaining 96 ids, so
  // this set answers all 127 and nothing falls back to a Blueprint glyph.
  // =====================================================================

  // ---- direction: every arrow is two straight rules, no curve -----------
  'arrow-left': '<path d="M20 12H4" /> <path d="m10 6-6 6 6 6" />',
  'arrow-right': '<path d="M4 12h16" /> <path d="m14 6 6 6-6 6" />',
  'arrow-up-right': '<path d="M7 17 17 7" /> <path d="M8 7h9v9" />',
  chev: '<path d="m9 5 7 7-7 7" />',
  'chevron-down': '<path d="m5 9 7 7 7-7" />',
  'chevron-left': '<path d="m15 5-7 7 7 7" />',
  'chevron-up': '<path d="m5 15 7-7 7 7" />',
  'chevrons-right': '<path d="m5 6 6 6-6 6" /> <path d="m13 6 6 6-6 6" />',
  'circle-arrow-up':
    '<circle cx="12" cy="12" r="8.5" /> <path d="M12 17.2V7.4" />' +
    ' <path d="m7.5 11.9 4.5-4.5 4.5 4.5" />',
  play: '<path d="M6 3.5 20.5 12 6 20.5Z" />',
  reply:
    '<path d="M20.5 19v-2.5a4 4 0 0 0-4-4H4" /> <path d="m9.5 18-6-5.5 6-5.5" />',
  'undo-2':
    '<path d="m9 14.5-5-5 5-5" />' +
    ' <path d="M4 9.5h10.5a5.5 5.5 0 0 1 0 11H11" />',
  'rotate-cw': ROTATE_ARROW,
  tack: ROTATE_ARROW,
  'refresh-cw':
    '<path d="M3.5 12a8.5 8.5 0 0 1 14.4-6.1L21 8.5" /> <path d="M21 3.5V9h-5.5" />' +
    ' <path d="M20.5 12a8.5 8.5 0 0 1-14.4 6.1L3 15.5" /> <path d="M3 20.5V15h5.5" />',
  'list-restart':
    '<path d="M3 5h18" /> <path d="M3 12h7" /> <path d="M3 19h7" />' +
    ' <path d="M20.7 15.5a4.7 4.7 0 1 1-1.4-3.4l1.7 1.5" />' +
    ' <path d="M21 10.8V13.9h-3.1" />',
  'maximize-2':
    '<path d="M14 3.5h6.5V10" /> <path d="M20.5 3.5 13.5 10.5" />' +
    ' <path d="M3.5 20.5 10.5 13.5" /> <path d="M10 20.5H3.5V14" />',
  'minimize-2':
    '<path d="m14 10 6.5-6.5" /> <path d="M20 10h-6V4" />' +
    ' <path d="m3.5 20.5 6.5-6.5" /> <path d="M4 14h6v6" />',
  'external-link':
    '<path d="M13.5 3.5h7v7" /> <path d="M10 14 20.5 3.5" />' +
    ' <path d="M18 12.5v8H3.5V6h8" />',
  'log-in':
    '<path d="M10 17.5 15.5 12 10 6.5" /> <path d="M15.5 12H3" />' +
    ' <path d="M14 3.5h6.5v17H14" />',
  'log-out':
    '<path d="M16 17.5 21.5 12 16 6.5" /> <path d="M21.5 12H9" />' +
    ' <path d="M10 3.5H3.5v17H10" />',
  download:
    '<path d="M12 3v11.5" /> <path d="m7 9.5 5 5 5-5" />' +
    ' <path d="M3.5 15.5V21h17v-5.5" />',
  upload:
    '<path d="M12 15.5V4" /> <path d="m7 9 5-5 5 5" />' +
    ' <path d="M3.5 15.5V21h17v-5.5" />',
  send:
    '<path d="M21.5 2.5 2.5 9.5l8 3.5 3.5 8Z" /> <path d="m10.5 13 11-10.5" />',

  // ---- marks: the bare glyphs, shared with the status ladder ------------
  check: CHECK_MARK,
  x: CROSS_MARK,
  ban: '<circle cx="12" cy="12" r="8.5" /> <path d="M6 18 18 6" />',
  'circle-x':
    '<circle cx="12" cy="12" r="8.5" /> <path d="m8.5 8.5 7 7" /> <path d="m15.5 8.5-7 7" />',
  'circle-help':
    '<circle cx="12" cy="12" r="8.5" />' +
    ' <path d="M9.2 9.6a2.8 2.8 0 0 1 5.6 0c0 1.9-2.8 2.1-2.8 3.9" />' +
    ' <path d="M11.2 16.6h1.6" />',
  ellipsis: '<path d="M4 12h2" /> <path d="M11 12h2" /> <path d="M18 12h2" />',
  square: '<path d="M3.5 3.5h17v17h-17Z" />',
  star:
    '<path d="m12 2.5 2.9 6.3 6.9.8-5.1 4.7 1.4 6.8L12 17.6l-6.1 3.5 1.4-6.8-5.1-4.7 6.9-.8Z" />',
  zap: '<path d="M13.5 2.5 4 13.5h6.5L10 21.5 20 10.5h-6.5Z" />',
  assistant:
    '<path d="M12 2.5 14.4 9.6 21.5 12 14.4 14.4 12 21.5 9.6 14.4 2.5 12 9.6 9.6Z" />' +
    ' <path d="M19.5 2.5v3" /> <path d="M18 4h3" />',
  shield: '<path d="M12 2.5 20 5.5v7l-8 9-8-9v-7Z" />',
  flag: '<path d="M5 21.5V3" /> <path d="M5 4.5h13.5l-3 4 3 4H5" />',
  bookmark: '<path d="M6 3h12v18l-6-5-6 5Z" />',
  pin:
    '<path d="M12 15.5V22" /> <path d="M8 2.5h8v6l3 4.5v2.5H5v-2.5l3-4.5Z" />',

  // ---- plates and panels: hard corners, never an rx --------------------
  'panels-top-left':
    '<path d="M3 3.5h18v17H3Z" /> <path d="M3 8.5h18" /> <path d="M9 8.5v12" />',
  'square-terminal':
    '<path d="M3.5 3.5h17v17h-17Z" /> <path d="m7.5 9 2.5 2.5-2.5 2.5" />' +
    ' <path d="M12.5 15h4.5" />',
  copy: '<path d="M8 8h13v13H8Z" /> <path d="M16 8V3H3v13h5" />',
  'message-square': '<path d="M3 3.5h18v13h-11l-5 4.5v-4.5H3Z" />',
  mail: '<path d="M2.5 4.5h19v15h-19Z" /> <path d="m2.5 6 9.5 7 9.5-7" />',
  camera:
    '<path d="M2.5 7.5h5L9 5h6l1.5 2.5h5V20h-19Z" />' +
    ' <circle cx="12" cy="13.5" r="3.6" />',
  eye:
    '<path d="M2.5 12C5.5 7.5 8.5 5.5 12 5.5s6.5 2 9.5 6.5c-3 4.5-6 6.5-9.5 6.5S5.5 16.5 2.5 12Z" />' +
    ' <circle cx="12" cy="12" r="3.2" />',
  search: '<circle cx="10.5" cy="10.5" r="7" /> <path d="m15.6 15.6 5.4 5.4" />',
  filter: '<path d="M3 4h18l-7 8v8l-4-2v-6Z" />',
  house:
    '<path d="M3 11 12 3l9 8" /> <path d="M5 9.5V21h14V9.5" />' +
    ' <path d="M9.5 21v-6h5v6" />',
  'folder-open':
    '<path d="M3 20V4h6l2 2.5h9V9" /> <path d="M3 20 6.5 10h17L20 20Z" />',
  workflow:
    '<path d="M3 3.5h7.5V11H3Z" /> <path d="M13.5 13h7.5v7.5h-7.5Z" />' +
    ' <path d="M6.75 11v6h6.75" />',
  clock: '<circle cx="12" cy="12" r="8.5" /> <path d="M12 6.5V12h4.5" />',
  hourglass:
    '<path d="M5 2.5h14" /> <path d="M5 21.5h14" />' +
    ' <path d="M6.5 2.5v3.4L12 12l5.5-6.1V2.5" />' +
    ' <path d="M6.5 21.5v-3.4L12 12l5.5 6.1v3.4" />',
  history:
    '<path d="M3.5 12a8.5 8.5 0 1 0 8.5-8.5A9.2 9.2 0 0 0 5.6 6.1L3.5 8.2" />' +
    ' <path d="M3.5 3.7v4.5H8" /> <path d="M12 7.2v5l3.6 2.1" />',

  // ---- documents and the register family -------------------------------
  book:
    '<path d="M5 3h14v18H5Z" /> <path d="M6.6 3v18" /> <path d="M8.6 3v18" />' +
    ' <path d="M11.4 8.5h5" /> <path d="M11.4 12h5" />',
  'book-open':
    '<path d="M12 6.5v14.5" /> <path d="M12 6.5 3 4.5V19l9 2Z" />' +
    ' <path d="M12 6.5 21 4.5V19l-9 2Z" />',
  library:
    '<path d="M2.5 5h3.2v15H2.5Z" /> <path d="M6.7 8h3.2v12H6.7Z" />' +
    ' <path d="M10.9 4h3.2v16h-3.2Z" /> <path d="m15.6 7.9 3-.9 3.4 11.2-3 .9Z" />',
  logbook:
    '<path d="M2 6h3.5" /> <path d="M2 10h3.5" /> <path d="M2 14h3.5" />' +
    ' <path d="M2 18h3.5" /> <path d="M5.5 2.5h16v19h-16Z" />' +
    ' <path d="M9 8h8" /> <path d="M9 12h8" /> <path d="M9 16h5.5" />',
  'notebook-tabs':
    '<path d="M2 6h3.5" /> <path d="M2 10h3.5" /> <path d="M2 14h3.5" />' +
    ' <path d="M2 18h3.5" /> <path d="M5.5 2.5h16v19h-16Z" />' +
    ' <path d="M15 2.5v19" /> <path d="M15 7.5h6.5" /> <path d="M15 12h6.5" />' +
    ' <path d="M15 16.5h6.5" />',
  scroll:
    '<path d="M4 3.5h16v3H4Z" /> <path d="M4 17.5h16v3H4Z" />' +
    ' <path d="M6 6.5v11" /> <path d="M18 6.5v11" />',
  'scroll-text':
    '<path d="M4 3.5h16v3H4Z" /> <path d="M4 17.5h16v3H4Z" />' +
    ' <path d="M6 6.5v11" /> <path d="M18 6.5v11" /> <path d="M9 9.5h6" />' +
    ' <path d="M9 12h6" /> <path d="M9 14.5h4" />',
  'clipboard-check': CLIPBOARD_CHECK,
  gate: CLIPBOARD_CHECK,
  'clipboard-list':
    '<path d="M5 4h14v17H5Z" /> <path d="M8.5 2.5h7V6h-7Z" />' +
    ' <path d="M8 11h1.6" /> <path d="M11.5 11h4.5" />' +
    ' <path d="M8 14.5h1.6" /> <path d="M11.5 14.5h4.5" />' +
    ' <path d="M8 18h1.6" /> <path d="M11.5 18h4.5" />',
  'badge-check':
    '<path d="M8.6 3h6.8L21 8.6v6.8L15.4 21H8.6L3 15.4V8.6Z" />' +
    ' <path d="m8 12.2 3 3.2 5.6-6.6" />',
  'file-pen':
    '<path d="M5 2.5h8.5L19 8v4.5" /> <path d="M5 2.5v19h7.5" />' +
    ' <path d="M13.5 2.5V8H19" /> <path d="m19.6 13.2 2.2 2.2-6 6h-2.2v-2.2Z" />',
  'square-pen':
    '<path d="M12 3.5H3.5v17h17V12" />' +
    ' <path d="m17.5 2.5 4 4-8.5 8.5-4.5 1 1-4.5Z" />',
  pencil:
    '<path d="M17 3 21 7 8.5 19.5 3 21l1.5-5.5Z" /> <path d="m4.5 15.5 4 4" />' +
    ' <path d="m14.5 5.5 4 4" />',

  // ---- machinery: the plant that runs the fleet -------------------------
  gear:
    '<path d="M10.2 2.5h3.6l.5 2.6 2.2 1.3 2.4-1.1 1.8 3.1-1.9 1.8v2.6l1.9 1.8-1.8 3.1-2.4-1.1-2.2 1.3-.5 2.6h-3.6l-.5-2.6-2.2-1.3-2.4 1.1-1.8-3.1 1.9-1.8v-2.6L3.3 8.4l1.8-3.1 2.4 1.1 2.2-1.3Z" />' +
    ' <circle cx="12" cy="12" r="3" />',
  hammer:
    '<path d="m3.5 20.5 8.6-8.6" /> <path d="M9.6 9.4 14.4 4.6l5.4 5.4-4.8 4.8Z" />' +
    ' <path d="m13 6 5.4 5.4" />',
  wrench:
    '<path d="M17.5 3a5.5 5.5 0 0 0-6.8 6.8l-7.9 7.9 3 3 7.9-7.9A5.5 5.5 0 0 0 21 6l-3.2 3.2-3-3Z" />',
  puzzle:
    '<path d="M4 4h6.2a1.8 1.8 0 1 1 3.6 0H20v6.2a1.8 1.8 0 1 0 0 3.6V20h-6.2a1.8 1.8 0 1 0-3.6 0H4Z" />',
  server:
    '<path d="M3 3.5h18v7H3Z" /> <path d="M3 13.5h18v7H3Z" />' +
    ' <path d="M6 7h1.6" /> <path d="M6 17h1.6" /> <path d="M15 7h3" />' +
    ' <path d="M15 17h3" />',
  database:
    '<path d="M12 3c4.4 0 8 1.3 8 3s-3.6 3-8 3-8-1.3-8-3 3.6-3 8-3Z" />' +
    ' <path d="M4 6v12c0 1.7 3.6 3 8 3s8-1.3 8-3V6" />' +
    ' <path d="M4 12c0 1.7 3.6 3 8 3s8-1.3 8-3" />',
  box: ISO_CRATE,
  cargo: ISO_CRATE + ' <path d="m7.5 4.75 9 4.5" />',
  container: ISO_CRATE + ' <path d="M15 9.9v9.6" /> <path d="M18 8.4v9.6" />',
  crane:
    '<path d="M7 21V3" /> <path d="M4 21h6" /> <path d="M2.5 3h19" />' +
    ' <path d="m7 7.5 8.5-4.5" /> <path d="M17 3v6" />' +
    ' <path d="M15.7 9h2.6L17 11.4Z" />',
  microscope:
    '<path d="M3 21.5h18" /> <path d="M6 18.5h9" />' +
    ' <path d="M8.5 6.5h5.5V11H8.5Z" /> <path d="M10 6.5V3h3v3.5" />' +
    ' <path d="M11.2 11v3.5" /> <path d="M9 14.5h4.5" />' +
    ' <path d="M14 8.5a7 7 0 0 1 0 10" />',
  'flask-conical':
    '<path d="M9 2.5V9L4 21h16L15 9V2.5" /> <path d="M8 2.5h8" />' +
    ' <path d="M6.5 15.5h11" />',
  stethoscope:
    '<path d="M5.5 2.5v4.5a4.5 4.5 0 0 0 9 0V2.5" /> <path d="M4 2.5h3" />' +
    ' <path d="M13 2.5h3" /> <path d="M10 11.5V15a5 5 0 0 0 10 0v-2.6" />' +
    ' <circle cx="20" cy="10" r="2.4" />',
  bug:
    '<path d="M8 8h8v5.5a4 4 0 0 1-8 0Z" />' +
    ' <path d="M9.5 8V6.5a2.5 2.5 0 0 1 5 0V8" /> <path d="M8 10.5H4" />' +
    ' <path d="M20 10.5h-4" /> <path d="m7.9 13.8-3.9 2.6" />' +
    ' <path d="m16.1 13.8 3.9 2.6" /> <path d="M9.6 5 8 2.8" />' +
    ' <path d="M14.4 5 16 2.8" />',
  scale:
    '<path d="M12 3v18" /> <path d="M4 6.5h16" /> <path d="M7 21h10" />' +
    ' <path d="M4 6.5 1.5 13.5h5Z" /> <path d="M20 6.5 17.5 13.5h5Z" />',

  // ---- the graph family: nodes are round, the wiring is ruled -----------
  'git-branch':
    '<circle cx="6" cy="18" r="2.4" /> <circle cx="18" cy="6" r="2.4" />' +
    ' <path d="M6 15.6V3" /> <path d="M15.6 6H9.5L6 9.5" />',
  'git-merge':
    '<circle cx="6" cy="6" r="2.4" /> <circle cx="18" cy="18" r="2.4" />' +
    ' <path d="M6 8.4V21" /> <path d="M15.6 18H9.5L6 14.5" />',
  'git-pull-request':
    '<circle cx="6" cy="6" r="2.4" /> <circle cx="18" cy="18" r="2.4" />' +
    ' <path d="M6 8.4V21" /> <path d="M12 6h6v9.6" /> <path d="m14.5 3.5 3 2.5-3 2.5" />',
  link:
    '<path d="m10.5 13.5-3 3a3.5 3.5 0 0 1-5-5l3-3a3.5 3.5 0 0 1 5 0" />' +
    ' <path d="m13.5 10.5 3-3a3.5 3.5 0 0 1 5 5l-3 3a3.5 3.5 0 0 1-5 0" />',
  knot:
    '<path d="M4.5 19.5 19.5 4.5" /> <path d="M4.5 4.5a10 10 0 0 1 10 10" />' +
    ' <path d="M19.5 19.5a10 10 0 0 1-10-10" />',

  // ---- the nautical register (this framework's own vocabulary) ---------
  anchor:
    '<path d="M12 7v14.5" /> <path d="M8 11h8" /> <circle cx="12" cy="4.2" r="2.2" />' +
    ' <path d="M4 13a8 8 0 0 0 16 0" /> <path d="M4 13H2.2" /> <path d="M20 13h1.8" />',
  ship:
    '<path d="M2.5 14.5h19l-3 6.5H5.5Z" /> <path d="M7 14.5V9h10v5.5" />' +
    ' <path d="M12 9V3" /> <path d="M12 3.5h4.5l-2 1.75H12" />',
  lighthouse:
    '<path d="M9 8h6l1.5 13h-9Z" /> <path d="M8.5 4.5h7V8h-7Z" />' +
    ' <path d="M12 1.5v3" /> <path d="M8.5 6.2H4.5" /> <path d="M15.5 6.2h4" />' +
    ' <path d="M7.8 12h8.4" /> <path d="M7.4 16.5h9.2" />',
  crew:
    '<circle cx="9.5" cy="7.2" r="3.6" />' +
    ' <path d="M2.5 21v-2.6l3.2-2.4h7.6l3.2 2.4V21" />' +
    ' <path d="M16.6 4.2a3.4 3.4 0 0 1 0 6" /> <path d="M18 16h1.3l2.2 2.4V21" />',
  'user-plus':
    '<circle cx="9.5" cy="7.2" r="3.6" />' +
    ' <path d="M2.5 21v-2.6l3.2-2.4h7.6l3.2 2.4V21" />' +
    ' <path d="M19.5 3v5" /> <path d="M22 5.5h-5" />',
  dividers:
    '<path d="M12 5.5 5.5 20.5" /> <path d="m12 5.5 6.5 15" />' +
    ' <circle cx="12" cy="3.8" r="1.8" /> <path d="M9.4 13h5.2" />',
  map:
    '<path d="m9 3 6 3 6-3v15l-6 3-6-3-6 3V6Z" /> <path d="M9 3v15" />' +
    ' <path d="M15 6v15" />',
  siren:
    '<path d="M7 18v-5.5a5 5 0 0 1 10 0V18" /> <path d="M5 18h14v3.5H5Z" />' +
    ' <path d="M12 2v2.5" /> <path d="M3.5 12H1.5" /> <path d="M22.5 12h-2" />' +
    ' <path d="m5.5 5.5 1.6 1.6" /> <path d="m18.5 5.5-1.6 1.6" />',
  bell:
    '<path d="M6 17V10a6 6 0 0 1 12 0v7" /> <path d="M4 17h16" />' +
    ' <path d="M10 20h4" />',

  // ── release-scope SECTION TYPES (#810 PR 4): one per renderer family,
  // cut in this set's own construction so the corpus TOC's section rows are
  // own-set under every style (icons:check refuses a fallback).
  'rs-prose':
    '<path d="M5 3.5h14v17H5Z" /> <path d="M8 8h8" /> <path d="M8 11.5h8" /> <path d="M8 15h5" />',
  'rs-paired-lists':
    '<path d="M3 4h7.5v16H3Z" /> <path d="M13.5 4H21v16h-7.5Z" /> <path d="M5.5 9h2.5" />' +
    ' <path d="M5.5 12.5h2.5" /> <path d="M16 9h2.5" /> <path d="M16 12.5h2.5" />',
  'rs-table':
    '<path d="M3 4h18v16H3Z" /> <path d="M3 9.5h18" /> <path d="M12 9.5V20" />' +
    ' <path d="M3 14.75h18" />',
  'rs-table-expansion':
    '<path d="M3 3.5h18v9H3Z" /> <path d="M3 8h18" /> <path d="M5.5 15.5h13v5h-13Z" />' +
    ' <path d="m10.5 9.8 1.5 1.5 1.5-1.5" />',
  'rs-rules':
    '<path d="M4.5 5.5h2v2h-2Z" /> <path d="M4.5 11h2v2h-2Z" /> <path d="M4.5 16.5h2v2h-2Z" />' +
    ' <path d="M10 6.5h10" /> <path d="M10 12h10" /> <path d="M10 17.5h6.5" />',
  'rs-state-machine':
    '<path d="M2.5 8.5h7v7h-7Z" /> <path d="M14.5 8.5h7v7h-7Z" /> <path d="M9.5 12h5" />' +
    ' <path d="m12.5 10 2 2-2 2" />',
  'rs-spec-cards':
    '<path d="M5.5 8.5h15v12h-15Z" /> <path d="M3.5 15.5v-12h13" /> <path d="M8.5 12.5h9" />' +
    ' <path d="M8.5 16h5.5" />',
  'rs-wireframe':
    '<path d="M3 4h18v16H3Z" /> <path d="M3 8.5h18" /> <path d="M6 11.5h6v5H6Z" />' +
    ' <path d="M15 12.5h3" /> <path d="M15 15.5h3" />',
};
